// redux/actions/junta/junta.js
import axios from "axios";
import {
  FETCH_JUNTA_FAILURE,
  FETCH_JUNTA_SUCCESS,
  FETCH_JUNTA_DETALLES_FAILURE,
  FETCH_JUNTA_DETALLES_SUCCESS,
} from "./types";
import { PATH } from "../../utils";

const SESSION_EXPIRED = "SESSION_EXPIRED";

const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const handle401 = (dispatch) => {
  localStorage.removeItem("token");
  localStorage.removeItem("tokenadmin");
  if (dispatch) dispatch({ type: SESSION_EXPIRED });
};

const api = axios.create({ baseURL: PATH, headers: { "Content-Type": "application/json" } });
const apiForm = axios.create({ baseURL: PATH, headers: { "Content-Type": "multipart/form-data" } });

// ========= Action creators =========
const fetchJuntaSuccess = (payload) => ({ type: FETCH_JUNTA_SUCCESS, payload });
const fetchJuntaFailure = (msg) => ({ type: FETCH_JUNTA_FAILURE, payload: { error: msg } });
const fetchJuntaDetallesSuccess = (payload) => ({ type: FETCH_JUNTA_DETALLES_SUCCESS, payload });
const fetchJuntaDetallesFailure = (msg) => ({ type: FETCH_JUNTA_DETALLES_FAILURE, payload: { error: msg } });

// ========= List (GET) =========
export const actionJuntaGet = () => async (dispatch) => {
  try {
    const res = await api.get("/junta-conciliacion", { headers: authHeader() });
    dispatch(fetchJuntaSuccess(res.data));
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    dispatch(fetchJuntaFailure(e?.message));
    throw e;
  }
};

// ========= List con filtros (POST) =========
export const actionJuntaCards = (filters = {}) => async (dispatch) => {
  try {
    const res = await api.post("/junta-conciliacion/cards", filters, { headers: authHeader() });
    dispatch(fetchJuntaSuccess(res.data));
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    dispatch(fetchJuntaFailure(e?.message));
    throw e;
  }
};

// ========= Get by ID (sin redux) =========
export const actionJuntaGetById = (id) => async (dispatch) => {
  try {
    const res = await api.get(`/junta-conciliacion/${id}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Get by ID (con redux) =========
export const actionJuntaGetByIdRedux = (id) => async (dispatch) => {
  try {
    const res = await api.get(`/junta-conciliacion/${id}`, { headers: authHeader() });
    dispatch(fetchJuntaDetallesSuccess(res.data));
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    dispatch(fetchJuntaDetallesFailure(e?.message));
    throw e;
  }
};

// ========= Create =========
export const actionJuntaCreate = (payload, filesMap = {}) => async (dispatch) => {
  const fd = new FormData();
  fd.append("payload", JSON.stringify(payload));

  Object.entries(filesMap).forEach(([key, files]) => {
    (Array.isArray(files) ? files : [files]).forEach((f) => {
      if (f?.originFileObj) fd.append(key, f.originFileObj, f.name);
    });
  });

  try {
    const res = await apiForm.post("/junta-conciliacion", fd, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Update =========
export const actionJuntaUpdateById = (id, payload, filesMap = {}) => async (dispatch) => {
  const fd = new FormData();
  fd.append("payload", JSON.stringify(payload));

  Object.entries(filesMap).forEach(([key, files]) => {
    (Array.isArray(files) ? files : [files]).forEach((f) => {
      if (f?.originFileObj) fd.append(key, f.originFileObj, f.name);
    });
  });

  try {
    const res = await apiForm.put(`/junta-conciliacion/${id}`, fd, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Delete =========
export const actionJuntaDeleteById = (id) => async (dispatch) => {
  try {
    const res = await api.delete(`/junta-conciliacion/${id}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Upload documento =========
export const actionJuntaUploadDocumento = (id_junta, file) => async (dispatch) => {
  const fd = new FormData();
  const fileObj = file?.originFileObj || file;
  fd.append("file", fileObj, file?.name || fileObj?.name);
  try {
    const res = await apiForm.post(`/junta-conciliacion/${id_junta}/documentos`, fd, {
      headers: authHeader(),
    });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Delete documento =========
export const actionJuntaDeleteDocumento = (id_junta, id_junta_documento) => async (dispatch) => {
  try {
    const res = await api.delete(
      `/junta-conciliacion/${id_junta}/documentos/${id_junta_documento}`,
      { headers: authHeader() }
    );
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Actividad (audit_log) =========
export const ID_MODULO_JUNTA = 8;

export const actionJuntaAuditLogGet = (id_junta, extra = {}) => async (dispatch) => {
  try {
    const qs = new URLSearchParams();
    qs.set("id_modulo", ID_MODULO_JUNTA);
    qs.set("id_key", id_junta);
    qs.set("limit", extra.limit || 100);
    const res = await api.get(`/audit-log?${qs.toString()}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Bitácora =========
export const actionJuntaBitacoraGet = (id_junta) => async (dispatch) => {
  try {
    const res = await api.get(`/junta-conciliacion/${id_junta}/bitacora`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Terminación =========
export const actionJuntaTerminacionGet = (id_junta) => async (dispatch) => {
  try {
    const res = await api.get(`/junta-conciliacion/${id_junta}/terminacion`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaTerminacionSave = (id_junta, payload) => async (dispatch) => {
  try {
    const res = await api.post(`/junta-conciliacion/${id_junta}/terminacion`, payload, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaTerminacionDelete = (id_junta) => async (dispatch) => {
  try {
    const res = await api.delete(`/junta-conciliacion/${id_junta}/terminacion`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaTerminacionUploadDoc = (id_junta, file) => async (dispatch) => {
  const fd = new FormData();
  fd.append("file", file, file.name);
  try {
    const res = await apiForm.post(`/junta-conciliacion/${id_junta}/terminacion/documentos`, fd, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaTerminacionSaveConstancia = (id_junta, { fecha_cumplimiento, notas_cumplimiento, file }) => async (dispatch) => {
  const fd = new FormData();
  fd.append("payload", JSON.stringify({ fecha_cumplimiento, notas_cumplimiento }));
  if (file) fd.append("archivo_constancia", file, file.name);
  try {
    const res = await apiForm.put(`/junta-conciliacion/${id_junta}/terminacion/constancia`, fd, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaTerminacionDeleteDoc = (id_junta, id_doc) => async (dispatch) => {
  try {
    const res = await api.delete(`/junta-conciliacion/${id_junta}/terminacion/documentos/${id_doc}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Etapas procesales =========
export const actionJuntaEtapaGet = (id_junta, num_etapa) => async (dispatch) => {
  try {
    const res = await api.get(`/junta-conciliacion/${id_junta}/etapas/${num_etapa}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaEtapaUpsert = (id_junta, num_etapa, payload) => async (dispatch) => {
  try {
    const res = await api.post(`/junta-conciliacion/${id_junta}/etapas/${num_etapa}`, payload, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaEtapaUploadDoc = (id_junta, num_etapa, audiencia_diferida, file) => async (dispatch) => {
  const fd = new FormData();
  const fileObj = file?.originFileObj || file;
  fd.append("file", fileObj, file?.name || fileObj?.name);
  try {
    const res = await apiForm.post(
      `/junta-conciliacion/${id_junta}/etapas/${num_etapa}/documentos?audiencia_diferida=${audiencia_diferida ?? 0}`,
      fd,
      { headers: authHeader() },
    );
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaEtapaDeleteDoc = (id_junta, num_etapa, id_doc) => async (dispatch) => {
  try {
    const res = await api.delete(`/junta-conciliacion/${id_junta}/etapas/${num_etapa}/documentos/${id_doc}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Defensa (apartado libre: notas + documentos) =========
export const actionJuntaDefensaGet = (id_junta) => async (dispatch) => {
  try {
    const res = await api.get(`/junta-conciliacion/${id_junta}/defensa`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaDefensaUpsert = (id_junta, texto) => async (dispatch) => {
  try {
    const res = await api.post(`/junta-conciliacion/${id_junta}/defensa`, { texto }, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaDefensaUploadDoc = (id_junta, file) => async (dispatch) => {
  const fd = new FormData();
  const fileObj = file?.originFileObj || file;
  fd.append("file", fileObj, file?.name || fileObj?.name);
  try {
    const res = await apiForm.post(
      `/junta-conciliacion/${id_junta}/defensa/documentos`,
      fd,
      { headers: authHeader() },
    );
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaDefensaDeleteDoc = (id_junta, id_doc) => async (dispatch) => {
  try {
    const res = await api.delete(`/junta-conciliacion/${id_junta}/defensa/documentos/${id_doc}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Registro de pruebas Ofrecidas/Admitidas (tab Proceso, Audiencia CDE) =========
export const actionJuntaPruebasRegistroGet = (id_junta) => async (dispatch) => {
  try {
    const res = await api.get(`/junta-conciliacion/${id_junta}/pruebas-registro`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaPruebasRegistroCreate = (id_junta, payload) => async (dispatch) => {
  try {
    const res = await api.post(`/junta-conciliacion/${id_junta}/pruebas-registro`, payload, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaPruebasRegistroUpdate = (id_junta, id_registro, payload) => async (dispatch) => {
  try {
    const res = await api.put(`/junta-conciliacion/${id_junta}/pruebas-registro/${id_registro}`, payload, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaPruebasRegistroDelete = (id_junta, id_registro) => async (dispatch) => {
  try {
    const res = await api.delete(`/junta-conciliacion/${id_junta}/pruebas-registro/${id_registro}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Agenda (integración con junta etapas) =========

// Crea un evento de agenda (multipart — el endpoint siempre espera Form)
export const actionJuntaAgendaCreate = (payload) => async (dispatch) => {
  const fd = new FormData();
  fd.append("payload", JSON.stringify(payload));
  try {
    const res = await apiForm.post("/agenda", fd, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// Actualiza un evento de agenda existente (JSON)
export const actionJuntaAgendaUpdate = (id_agenda, payload) => async (dispatch) => {
  try {
    const res = await api.put(`/agenda/${id_agenda}`, payload, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// Elimina un evento de agenda (soft-delete)
export const actionJuntaAgendaDelete = (id_agenda) => async (dispatch) => {
  try {
    const res = await api.delete(`/agenda/${id_agenda}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

// ========= Actuaciones (bitácora libre) — tab "Expediente" de Junta =========
export const actionJuntaActuacionesGet = (id_junta) => async (dispatch) => {
  try {
    const res = await api.get(`/junta-conciliacion/${id_junta}/actuaciones`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    return { items: [] };
  }
};

export const actionJuntaActuacionCreate = (id_junta, data) => async (dispatch) => {
  try {
    const res = await api.post(`/junta-conciliacion/${id_junta}/actuaciones`, data, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaActuacionUpdate = (id_junta, id_actuacion, data) => async (dispatch) => {
  try {
    const res = await api.put(`/junta-conciliacion/${id_junta}/actuaciones/${id_actuacion}`, data, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaActuacionDelete = (id_junta, id_actuacion) => async (dispatch) => {
  try {
    const res = await api.delete(`/junta-conciliacion/${id_junta}/actuaciones/${id_actuacion}`, { headers: authHeader() });
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};

export const actionJuntaActuacionUploadDocumento = (id_junta, id_actuacion, file, rol = null) => async (dispatch) => {
  const fd = new FormData();
  const fileObj = file?.originFileObj || file;
  fd.append("documento", fileObj, file?.name || fileObj?.name);
  if (rol) fd.append("rol", rol);
  try {
    const res = await apiForm.post(
      `/junta-conciliacion/${id_junta}/actuaciones/${id_actuacion}/documentos`,
      fd,
      { headers: authHeader() },
    );
    return res.data;
  } catch (e) {
    if (e?.response?.status === 401) handle401(dispatch);
    throw e;
  }
};
